/**
 * Frontend Sentry initialisation — guarded.
 *
 * No SDK init when REACT_APP_SENTRY_DSN is blank/missing — the entire
 * SDK loads but never opens its transport. We import statically so
 * webpack tree-shakes consistently (dynamic import would change
 * chunking and risk a runtime split-load fault on flaky mobile
 * networks).
 *
 * Behaviour highlights (matching iter-68 spec):
 *   • Errors + unhandled promise rejections → Sentry issues
 *   • React error boundary support (wrap App in <Sentry.ErrorBoundary>)
 *   • Session Replay: OFF (privacy + cost)
 *   • Performance traces: 5% sample rate (cheap, gives p95 visibility)
 *   • beforeSend hook masks any E.164 phone numbers in event payloads
 *     before transmission so PII never crosses the wire.
 *
 * Public surface:
 *   initSentry()      — call once at module-load. Idempotent. No-op
 *                       without DSN.
 *   isSentryEnabled() — boolean; useful for conditional UI (e.g.
 *                       "report a bug" link only when Sentry is on).
 *   setUserContext({id, role}) — call after login so events are
 *                                tagged. setUserContext(null) on
 *                                logout to scrub.
 *   captureEvent(name, data)  — typed breadcrumb (info, not error)
 *                               for ride-hail events the user wants
 *                               to see in Sentry as context.
 */
import * as Sentry from "@sentry/react";

let _enabled = false;

const _PHONE_RE = /\+[\d][\d\s\-\(\)]{6,18}\d/g;

function maskPhone(value) {
  if (typeof value !== "string") return value;
  const digits = value.replace(/[^\d+]/g, "");
  if (!digits.startsWith("+") || digits.length < 6) return value;
  // Match the backend mask: cc + middle elided + last 3
  const ccLen = "147".includes(digits[1]) ? 3 : 4;
  const cc    = digits.slice(0, ccLen);
  const last3 = digits.slice(-3);
  return `${cc} ••• ••• ${last3}`;
}

function scrubObject(obj) {
  if (typeof obj === "string") {
    return obj.includes("+") ? obj.replace(_PHONE_RE, (m) => maskPhone(m)) : obj;
  }
  if (Array.isArray(obj)) return obj.map(scrubObject);
  if (obj && typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      const klc = String(k).toLowerCase();
      if (klc.includes("password") || klc.includes("token") || klc.includes("authorization") || klc.includes("secret") || klc.includes("dsn")) {
        out[k] = "***REDACTED***";
      } else if (klc.includes("phone") || klc.includes("mobile") || klc.includes("msisdn")) {
        out[k] = typeof v === "string" ? maskPhone(v) : v;
      } else {
        out[k] = scrubObject(v);
      }
    }
    return out;
  }
  return obj;
}

export function initSentry() {
  if (_enabled) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // No-op — matches backend behaviour when SENTRY_DSN is blank.
    return;
  }
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "unknown",
      // 5% transaction sample — enough for p95 visibility without
      // blowing past Sentry free-tier quota.
      tracesSampleRate: 0.05,
      // Replay is explicitly OFF per spec (privacy + cost).
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Disable auto-captured console.error breadcrumbs from third-party
      // map / Google libs that produce noisy false-positive events.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured",
      ],
      beforeSend(event) {
        try {
          return scrubObject(event);
        } catch {
          return event;
        }
      },
    });
    _enabled = true;
  } catch {
    // Never let observability code break the app boot path.
  }
}

export function isSentryEnabled() {
  return _enabled;
}

export function setUserContext(user) {
  if (!_enabled) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    // intentionally NO email, NO phone — only opaque id + role tag.
  });
  Sentry.setTag("role", user.role || "unknown");
}

export function captureEvent(name, data = {}) {
  if (!_enabled) return;
  try {
    Sentry.addBreadcrumb({
      category: "ridehail",
      message: name,
      level: "info",
      data: scrubObject(data),
    });
  } catch {
    /* swallow */
  }
}

// Re-export the ErrorBoundary so App.js doesn't need a Sentry import.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
